import { Box, OutlinedInput } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import _ from "lodash";
import EditDailyHourDialog from '../EditDailyHourDialog/index'
import { showAlertSnackbar } from "../../../../reducers/uiSlice";

const useStyles = makeStyles((theme) => ({
    margin: {
        margin: theme.spacing(1),
    },
    group: {
        display: 'flex',
        alignItems: 'center'
    },
    item: {
        color: 'white'
    },
    formControl: {
        marginBottom: theme.spacing(1),
        minWidth: 220,
    },
    leftSection: {
        paddingTop: theme.spacing(3),
        display: 'flex',
        justifyContent: 'space-evenly',
        alignItems: 'left',
    },
    inputfield: {
        border: '2px solid orange',
        borderRadius: '5px',
        padding: '5px',
        fontSize: '16px',
        width: '40%',
    },
    dialog: {
        overflow: 'auto',
        maxHeight: '60vh',
        '&::-webkit-scrollbar': {
            width: '7px',
            backgroundColor: 'lightgray',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'gray',
            borderRadius: '4px',
            height: '60px'
        },
    }
}));

function DailyLogTable(props) {
    const { t } = useTranslation('payrollAdjustment');
    const classes = useStyles();
    const dispatch = useDispatch();
    const PAGE_SIZE = 20;
    const [open, setOpen] = useState(false); // Add this state variable for managing the dialog
    const [selectedRow, setSelectedRow] = useState(null); // Add this state variable to store the selected row data

    const handleOpen = (params, event) => {
        // Ensure log_id is valid before opening the dialog
        const isValidLogId = params.row.log_id && params.row.log_id !== 0;
    
        if (isValidLogId) {
            if (event.target.dataset.field !== 'date_hours') {
                setSelectedRow(params.row); // Store the selected row's data
                if (params.row.date_hours !== '') {
                    setOpen(true);
                }
                // Open the dialog
            }
        } else {

            dispatch(showAlertSnackbar({
                title: t('daily_log'),
                message: t('dialog:update_daily_log_rule'),
                type: 'error'
            }));
        }
    };
    

    const handleClose = () => {
        setOpen(false); // Close the dialog
    };

    const payrollDetailsColumns = [
        { field: 'log_id', headerName: t('log_id'), hide: true },
        {
            field: 'driver_id',
            headerName: t('driver_id'),
            maxWidth: 100,
            headerAlign: 'center',
            align: 'center',
            flex: 0.4
        },
        {
            field: 'name',
            headerName: t('driver_name'),
            maxWidth: 100,
            headerAlign: 'center',
            align: 'center',
            flex: 0.6,
        },
        {
            field: 'total_count',
            headerName: t('deliveries_daily'),
            width: 150,
            headerAlign: 'center',
            align: 'center',
            flex: 0.6,
        },
        {
            field: 'milage_daily',
            headerName: t('milage_daily'),
            width: 150,
            headerAlign: 'center',
            align: 'center',
            flex: 0.6,
        },
        {
            field: 'week_hours',
            headerName: t('hours_weekly'),
            width: 150,
            headerAlign: 'center',
            align: 'center',
            flex: 0.8,
        },
        {
            field: 'semi_month',
            headerName: t('total_hours'),
            width: 150,
            headerAlign: 'center',
            align: 'center',
            flex: 0.6,
        },
        {
            field: 'date_hours',
            headerName: t('daily_hours'),
            width: 150,
            headerAlign: 'center',
            align: 'center',
            disableClickEventBubbling: true,
            flex: 0.6,
            renderCell: (params) => {
                return (
                    <div>
                        <OutlinedInput
                            id="paroll_dailylog_table_outlinedinput"
                            value={params.row.date_hours || ''}
                            className={classes.inputfield}
                            onChange={(event) => handleDailyHourChange(event, params)}
                            placeholder="N/A"
                            fullWidth
                            margin="dense"
                            onClick={(event) => event.stopPropagation()} // Stop event propagation here
                            inputProps={{
                                style: {
                                    textAlign: "center",
                                    color: "orange"
                                }
                            }}
                        />
                    </div>
                );
            },

        },
    ];

    const handleDailyHourChange = (event, params) => {
        const newDailyHours = event.target.value;

        const newAllDriverDetails = [...props.allDriverDetails];

        // Find the index of the item to update
        const itemIndex = newAllDriverDetails.findIndex(item => item.driver_id === params.row.driver_id);

        // Update the value
        if (itemIndex >= 0) {
            newAllDriverDetails[itemIndex].date_hours = newDailyHours;
        }
        // Here, implement a function in the parent component to update allDriverDetails
        props.updateAllDriverDetails(newAllDriverDetails);

        // Check if the edited driver is in the selected items
        const selectedItemIndex = props.selectedItems
            ? props.selectedItems.findIndex(item => item.driver_id === params.row.driver_id)
            : -1;

        if (selectedItemIndex >= 0) {
            const newSelectedItems = [...props.selectedItems];
            newSelectedItems[selectedItemIndex].date_hours = newDailyHours;
            props.setSelectedItems(newSelectedItems);
        }
    };


    const rows = Array.isArray(props.allDriverDetails)
        ? props.allDriverDetails.map((item) => ({
            id: item.driver_id, //use driver_id as id proptory
            driver_id: item.driver_id || 0,
            log_id: item.log_id || 0,
            name: item.name || 'N/A',
            total_count: item.total_count || 0,
            milage_daily: parseFloat(item.milage_daily || 0).toFixed(2), // Keep only two decimal places
            week_hours: parseFloat(item.week_hours || 0).toFixed(2), // Keep only two decimal places
            semi_month: parseFloat(item.semi_month || 0).toFixed(2),
            date_hours: item.date_hours || 0,
        }))
        : [];

    return (
        <Box style={{ margin: 30, paddingRight: 40 }}>
            <DataGrid
                columns={payrollDetailsColumns}
                rows={rows}
                pagination
                pageSize={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                checkboxSelection
                loading={props.isDataLoading}
                onRowClick={(params, event) => handleOpen(params, event)}
                rowCount={rows.length || 0}
                onSelectionModelChange={(newSelection) => {
                    const selectedItems = newSelection.map((itemId) => {
                        const selectedRow = rows.find((row) => row.id === itemId);
                        return {
                            ...selectedRow,
                            date_hours: selectedRow.date_hours || 0,
                        };
                    });
                    props.setSelectedItems(selectedItems);
                }}
                style={{ height: '75vh' }}
            />
            <EditDailyHourDialog
                driverDeatils={selectedRow}
                closeDatailyLogDialog={handleClose}
                openDatailyLogDialog={open}
                handleSearch={props.handleSearch}
            />
        </Box>
    );
}

export default DailyLogTable;
